import { TLCEvents } from "../../../js/lib/events/events";
import { TLCLogger } from "../../../js/lib/logger";

export class Speech {
  constructor(transcriptions, options) {
    if (!this.supportsMic()) {
      return;
    }

    this.isEnabled = true;

    this._transcriptions = transcriptions;

    Object.assign(this, this._defaultOptions, options);

    this.locale = this.getLocale();

    this.createSpeechRecognition();
  }

  getLocale() {
    const lang = document.querySelector("html").lang;
    return lang ? lang.replace("_", "-") : "en-US";
  }

  toggle() {
    if (this.isEnabled) {
      if (this._isListening) {
        this.stop();
      } else {
        this.start();
      }
    }
  }

  /**
   * Create the speech recognition object and attach events with options.
   */
  createSpeechRecognition() {
    var _this = this;

    //noinspection JSUnresolvedFunction,JSPotentiallyInvalidConstructorUsage
    this._recognition = new window.webkitSpeechRecognition();
    this._recognition.continuous = this.continuous;
    this._recognition.interimResults = this.interimResults;

    /**
     * When .start() is called on recognition and the mic is ready to be used.
     * @param {webkitSpeechRecognitionEvent} event
     */
    this._recognition.onstart = function (event) {
      _this._onstart(event);
    };

    /**
     * When .stop() is called on recognition and when the
     * @param {webkitSpeechRecognitionEvent} event
     */
    this._recognition.onend = function (event) {
      _this._onend(event);
    };

    /**
     * an error handler for the speech recognition object
     * @param {webkitSpeechRecognitionEvent} event
     */
    this._recognition.onerror = function (event) {
      _this._onerror(event);
    };

    /**
     * when a result is received, either an interim or full result (details in event object)
     * @param {webkitSpeechRecognitionEvent} event
     */
    this._recognition.onresult = function (event) {
      _this._onresult(event);
    };
  }
  /**
   * Internal start handler, fires when the recognition has fired its start event and is listening
   * AND that it had permission to be started.
   * @param {webkitSpeechRecognitionEvent} event
   * @private
   */
  _onstart(event) {
    this.onStart(event);
  }
  /**
   * Internal end hander, fires when the recognition has fired its end event and its no longer listening.
   * @param {webkitSpeechRecognitionEvent} event
   * @private
   */
  _onend(event) {
    this._isListening = false;

    this.onEnd(event);
  }
  /**
   * Internal error handler, fires when the recognition encounters an error.
   * @param {webkitSpeechRecognitionEvent} event
   * @private
   */
  _onerror(event) {
    if (event.error == "no-speech") {
      this.onError("info_no_speech", event);
    }

    if (event.error == "audio-capture") {
      this.onError("info_no_microphone", event);
    }

    if (event.error == "not-allowed") {
      if (event.timeStamp - this._start_timestamp < 100) {
        this.onError("info_blocked", event);
      } else {
        this.onError("info_denied", event);
      }
    }
  }
  /**
   * error handler with the interpreted error
   * @param {string} errorName
   * @param {webkitSpeechRecognitionEvent} event
   */
  onError(errorName, event) {
    TLCLogger.log("SPEECH ERROR OCCURRED: ", errorName, event);
  }
  /**
   * Result handler for the speech recognition object, interprets the transcripts and calls the external callbacks
   * @param {webkitSpeechRecognitionEvent} event
   * @private
   */
  _onresult(event) {
    var interim_transcript = "";

    if (typeof event.results == "undefined") {
      this._recognition.onend = null;
      this._recognition.stop();

      this.trigger("info_unsupported");

      return;
    }

    var wasFinal = false;

    // go through every kind of event
    for (var i = event.resultIndex; i < event.results.length; ++i) {
      var result = event.results[i];

      var transcript = result[0].transcript;

      if (result.isFinal) {
        wasFinal = true;
        this._onFullResult(transcript, event);
      } else {
        interim_transcript += transcript;
      }
    }

    if (!wasFinal) {
      this._onInterimResult(interim_transcript, event);
    }
  }
  /**
   * set the recognition to start listening
   * @param {webkitSpeechRecognitionEvent} event
   * @private
   */
  _startListening(event) {
    if (this._isListening) {
      this.stop();
    }

    this._recognition.lang = this.locale;
    this._recognition.start();
    this._start_timestamp = Date.now();

    this._isListening = true;
  }
  /**
   * Start trigger, will render the details template
   */
  start() {
    this._startListening();
  }
  /**
   * Stop listening, will stop speech recognition and will fire onStop then onEnd
   */
  stop() {
    this._recognition.stop();
    this._isListening = false;

    this.onStop();
  }
  /**
   *
   * @param {string} key which key in this._transcriptions should be
   * @param {string} transcript dictation of what the user spoke
   */
  triggerTranscript(key, transcript) {
    var event = this._transcriptions[this.locale][key];

    if (typeof event === "function") {
      event.call(this, transcript);
    } else if (event) {
      TLCEvents.trigger(event, {
        fromVoice: true,
        voiceTranscript: transcript,
      });
    } else {
      TLCLogger.log(
        "SPEECH: locale=" + this.locale + "; bad key/event? ",
        key,
        event
      );
    }
  }
  /**
   *
   * @param transcript
   */
  interpretTranscript(transcript) {
    var _this = this;
    var triggeredEvent = false;
    var transcriptions = this._transcriptions[this.locale];

    for (var key in transcriptions) {
      if (Object.prototype.hasOwnProperty.call(transcriptions, key)) {
        if (transcript.indexOf(key) > -1) {
          triggeredEvent = true;
          this.triggerTranscript(key, transcript);
        }
      }
    }

    if (!triggeredEvent) {
      this.triggerTranscript("DEFAULT", transcript);
    }
  }
  /**
   * Internal handler for an interim result
   * @param {String} transcript
   * @param {webkitSpeechRecognitionEvent} event
   * @private
   */
  _onInterimResult(transcript, event) {
    this.onInterimResult(transcript, event);
  }
  /**
   * Internal handler for getting a full result.
   * @param {string} transcript
   * @param {webkitSpeechRecognitionEvent} event
   * @private
   */
  _onFullResult(transcript, event) {
    var _this = this;

    setTimeout(function () {
      _this.stop();
    }, this._delay);

    this.interpretTranscript(transcript);

    _this.onFullResult(transcript, event);
  }
  /**
   * Whether or not the microphone feature is supported
   * @returns {boolean}
   */
  supportsMic() {
    return "webkitSpeechRecognition" in window;
  }
}

/**
 *
 * @type {Object}
 * @private
 */
Speech.prototype._defaultOptions = {
  interimResults: true,
  continuous: true,
  onEnd: function (event) {},
  onStart: function (event) {},
  onStop: function () {},
  onFullResult: function (event, transcript) {},
  onInterimResult: function (event, transcript) {},
};

/**
 *
 * @type {webkitSpeechRecognition}
 * @private
 */
Speech.prototype._recognition = null;

/**
 * when finished interpreting, how long to wait before firing the stop event
 * @type {number}
 * @private
 */
Speech.prototype._delay = 500;

/**
 * a date time stamp of when the mic started listening.
 *
 * @type {number}
 * @private
 */
Speech.prototype._start_timestamp = null;

/**
 * whether or not the microphone is on
 *
 * @type {boolean}
 * @private
 */
Speech.prototype._isListening = false;

/**
 * whether or not this module is enabled, if disabled all events do not fire
 *
 * @type {boolean}
 * @private
 */
Speech.prototype._isEnabled = true;
